import format from 'date-fns/format'
import fromUnixTime from 'date-fns/fromUnixTime'
import getUnixTime from 'date-fns/getUnixTime'
import de from 'date-fns/locale/de'
import enGB from 'date-fns/locale/en-GB'
import enUS from 'date-fns/locale/en-US'
import es from 'date-fns/locale/es'
import fr from 'date-fns/locale/fr'
import nl from 'date-fns/locale/nl'
import React, { useCallback, useEffect } from 'react'

//
import { Hero, HeroDual, Wistia, SelectedCategories, ButtonNew } from '../../../storybook'
import BlogFilters from '../../../storybook/BlogFilters'
import { NavLink } from '../../../storybook/helpers'
import { getWistiaAspectRatio } from '../../../utils/common'
import { getMicroCopy } from '../../../utils/microcopy'
import { renderRichTextLine } from '../../../utils/text'
import { LocationContext, SiteDataContext } from '../../page-wrapper'

import ArticlesListing from './articles'
import { useMicroCopyData } from '@/src/context/MicroCopyContext'
import { BUTTON_VARIANT } from '../../../storybook/ButtonNew/ButtonNew'
import { Image as DatoImage } from 'react-datocms'

export const validLocales = { en: enGB, de: de, fr: fr, es: es, nl: nl, us: enUS }

const articlesQuery = `
  query($locale: SiteLocale!, $marketId:[ItemId]! ) {
    allArticleModels(
      locale: $locale,
      filter: {enabledMarkets: { anyIn: $marketId }, enabledLocales: { eq: true }},
      first: 250,
      orderBy: articlePublishedDate_DESC) {
        id
        title:_allTitleLocales {
          locale
          value
        }
        articlePublishedDate
        description:_allDescriptionLocales {
          locale
          value
        }
        slug:_allSlugLocales {
          locale
          value
        }
        readingTime
        image {
          url
          height
          width
          responsiveImage {
            srcSet
            sizes
            src
            width
            height
            aspectRatio
            alt
            title
            bgColor
            base64
          }
        }
        video {
          id
          videoId
          videoSource
        }
        categories {
            id
            name
            slug
        }
        enabledLocales
        enabledMarkets {
            id
            title
            slugPrefix
        }
    }
  }
`

const BlockBlogTop = ({ data, data: { topTitle, topDescription, articleHighlight } }) => {
  const { articleCategories, lang, market } = React.useContext(SiteDataContext)

  const location = React.useContext(LocationContext)
  const { slugPrefix } = location

  const [articles, setArticles] = React.useState([])
  const [filteredArticles, setFilteredArticles] = React.useState(null)

  const allCategories = []
  articleCategories?.forEach((category) => {
    const { id, slug, name, locale, order } = category
    allCategories.push({ id, slug, name, locale, order, selected: false })
  })

  const [categories, setCategories] = React.useState(allCategories)

  const microCopyData = useMicroCopyData()

  const articleHighlightToShow =
    articleHighlight?.enabledLocales === null || articleHighlight?.enabledLocales === true
      ? articleHighlight
      : false

  const fetchArticles = (ignore) => {
    const variables = {
      locale: data.extra.locale,
      marketId: [market.id]
    }
    const currentSlugPrefix = (slugPrefix || '').replace(/\//gim, '')
    let locale = currentSlugPrefix.split('-')[0] || 'en'
    if (!validLocales[locale]) {
      locale = 'en'
    }

    const fetchData = async () => {
      try {
        const response = await fetch('/api/dato-fetch', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ query: articlesQuery, variables })
        })

        const res = await response.json()
        if (response.ok) {
          const articlesFetched =
            res?.allArticleModels?.map((article) => {
              const timestamp = getUnixTime(new Date(article.articlePublishedDate))
              const date = fromUnixTime(timestamp)
              return {
                ...article,
                timestamp,
                date: format(date, 'MMMM d, y', { locale: validLocales[locale] }),
                categories: article?.categories,
                title:
                  article?.title?.find((t) => t.locale === locale)?.value ||
                  article?.title?.find((t) => t.locale === 'en')?.value,
                description:
                  article?.description?.find((d) => d.locale === locale)?.value ||
                  article?.description?.find((d) => d.locale === 'en')?.value,
                slug:
                  article?.slug?.find((s) => s.locale === locale)?.value ||
                  article?.slug?.find((s) => s.locale === 'en')?.value
              }
            }) || []

          const articlesToSet = articlesFetched
            ?.filter((article) => article?.slug !== articleHighlightToShow?.slug)
            ?.filter((article) => {
              return article.enabledLocales === null || article.enabledLocales === true
            })
            ?.filter((article) => {
              if (
                article?.enabledMarkets === null ||
                (Array.isArray(article?.enabledMarkets) && article?.enabledMarkets.length === 0)
              ) {
                return true
              }

              const slugPrefixes = article?.enabledMarkets.map((i) => i.slugPrefix) || []
              return slugPrefixes.includes(currentSlugPrefix)
            })
          if (!ignore) {
            setArticles(articlesToSet)
          }
        } else {
          console.error('Failed to fetch data:', res.error)
          // Handle error appropriately
        }
      } catch (error) {
        console.error('Error fetching data:', error)
        // Handle error appropriately
      }
    }

    fetchData()
  }

  useEffect(() => {
    let ignore = false
    fetchArticles(ignore)
    return () => {
      ignore = true
    }
  }, [])

  useEffect(() => {
    let selectedCategories

    if (categories) {
      selectedCategories = categories.filter((cat) => cat.selected)
    }

    const artCollection = articles?.filter((article) => {
      if (selectedCategories?.length) {
        return selectedCategories.some((sc) =>
          article?.categories.find((ac) => sc.slug === ac.slug)
        )
      } else {
        return true
      }
    })
    setFilteredArticles(artCollection || [])
  }, [categories, articles])

  const handleOnCategoryClick = useCallback(
    (categoryId) => {
      const selectedCategories = []
      const updateCategories = categories.map((category) => {
        let selected = category.selected
        if (category.id === categoryId) {
          selected = !category.selected

          if (selected === true) selectedCategories.push(category.slug)
        }
        return { ...category, selected }
      })
      setCategories(updateCategories)
    },
    [categories]
  )

  const handleClear = useCallback(() => {
    const updateCategories = categories.map((category) => {
      return { ...category, selected: false }
    })
    setCategories(updateCategories)
  }, [categories])

  return (
    <>
      <section className="l-section c-blog-top__header">
        <Hero
          title={topTitle}
          background={'c-hero--bg-dark c-hero--bg-dark--no-img'}
          content={renderRichTextLine(topDescription)}
          noPaddingTop
        />
        <BlogFilters categories={categories} setCategories={setCategories} />
      </section>
      <div className="c-blog-top__content">
        {categories.find((category) => category.selected === true) ? (
          <SelectedCategories
            totalArticles={(filteredArticles && filteredArticles.length) || 0}
            microCopyData={microCopyData[lang]}
            selectedCategories={categories.filter((category) => category.selected === true)}
            handleOnCategoryClick={handleOnCategoryClick}
            handleClear={handleClear}
          />
        ) : null}
        {articleHighlightToShow && (
          <HeroDual
            headingType="h2"
            classes="c-blogFilters__heroDual"
            title={articleHighlightToShow?.title}
            text={articleHighlightToShow?.description}
            eyebrow={articleHighlightToShow?.categories?.map((cat) => cat.name)?.join(', ')}
            media={
              articleHighlightToShow?.video ? (
                <Wistia
                  videoId={articleHighlightToShow?.video?.videoId}
                  videoSource={articleHighlightToShow?.video?.videoSource}
                  thumbnail={
                    articleHighlightToShow?.video?.thumbnail || articleHighlight?.image || null
                  }
                  aspectRatioX={getWistiaAspectRatio(articleHighlightToShow?.video?.aspectRatio).x}
                  aspectRatioY={getWistiaAspectRatio(articleHighlightToShow?.video?.aspectRatio).y}
                  cover={articleHighlightToShow?.video?.cover}
                  autoPlay={articleHighlightToShow?.video?.autoPlay}
                  controlsVisibleOnLoad={articleHighlightToShow?.video?.controlsVisibleOnLoad}
                  muted={articleHighlightToShow?.video?.muted}
                  transparentBackground={articleHighlightToShow?.video?.transparentBackground}
                  popover={articleHighlightToShow?.video?.popover}
                  hideControls={articleHighlightToShow?.video?.hideControls}
                  playText={articleHighlightToShow?.video?.playText}
                />
              ) : (
                <NavLink
                  to={`${slugPrefix}blog/${articleHighlight?.categories?.[0]?.slug}/${articleHighlight.slug}`}
                >
                  <div
                    className="u-position-relative"
                    style={{
                      maxHeight: '100%',
                      height: '472px',
                      display: 'grid',
                      placeItems: 'center'
                    }}
                  >
                    <DatoImage
                      data={articleHighlight?.image.responsiveImage}
                      imgStyle={{
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                </NavLink>
              )
            }
            greenEyebrow
          >
            <ButtonNew
              variant={BUTTON_VARIANT.GREEN}
              url={`${slugPrefix}blog/${articleHighlight?.categories?.[0]?.slug}/${articleHighlight.slug}`}
            >
              {getMicroCopy({ key: `readArticle`, data: microCopyData[lang] })}
            </ButtonNew>
          </HeroDual>
        )}
        {filteredArticles && (
          <>
            <ArticlesListing articles={filteredArticles} limit={12} slugPrefix={slugPrefix} />
          </>
        )}
      </div>
    </>
  )
}

export default BlockBlogTop
